// UserDetails.js
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    User,
    Mail,
    Phone,
    MapPin,
    Calendar,
    Download,
    Edit,
    X,
    SquareChevronLeft,
    Key,
    History,
    CheckCircle,
    XCircle,
    Clock
} from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from '../../i18n/i18n';
import api from '../../api/axios';
import UserModal from '../../components/Modals/UserModal';


const UserDetails = ({ lang }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation(lang);

    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [sessionLogs, setSessionLogs] = useState([]);
    const [showUserModal, setShowUserModal] = useState(false);
      const [showResetModal, setShowResetModal] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [resetMessage, setResetMessage] = useState('');

    // Fetch user details from API
    const fetchUserDetails = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/users/${id}`);
            const data = response.data.result;

            // Transform API data to match our structure
            const transformedUser = {
                id: data.id,
                full_name: data.full_name,
                email: data.email,
                phone: data.phone || 'N/A',
                status: data.status,
                address: data.address || 'N/A',
                created_at: data.created_at,
                last_login: data.sessionLog?.[0]?.date || 'N/A',
                // role: data.role || 'No role',
                // permissions: data.permissions || []
            };

            setUser(transformedUser);

            // Set session logs if available
            if (data.sessionLog && Array.isArray(data.sessionLog)) {
                setSessionLogs(data.sessionLog.slice(0, 10)); // Show last 10 sessions
            }

        } catch (error) {
            console.error('Error loading user details:', error);
            toast.error(t('errorLoadingUser'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchUserDetails();
        }
    }, [id]);

    const handleEdit = () => {
        setShowUserModal(true);
    };

    const handleEditUser = async (userData) => {
  try {    
    if (!userData?.name) {
      toast.error(t('noUserSelected'));
      return;
    }
    
    // Appel API pour modifier un utilisateur
    const response = await api.post(`/users/${id}/update`, userData);
    
    if (response.data?.request?.status === false) {
      toast.error(response.data.request.message);
      return;
    }
    
    
    toast.success(t('userUpdatedSuccess'));
    fetchUserDetails();
    setShowUserModal(false);
    
  } catch (error) {
    console.error('Erreur lors de la modification:', error);
    
    // Gestion d'erreur
    let errorMessage = t('editUserError');
    if (error.response?.data?.request?.message) {
      errorMessage = error.response.data.request.message;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    }
    
    toast.error(errorMessage);
  }
};

    const toggleUserStatus = async () => {
        try {
            const newStatus = user.status === 'enabled' ? 'disabled' : 'enabled';

            // This endpoint might need to be adjusted based on your API
            await api.post(`/users/activate`, {
                dataUser: [id],
                status: newStatus
            });

            setUser(prev => ({ ...prev, status: newStatus }));
            toast.success(t('userStatusUpdated'));
        } catch (error) {
            console.error('Error updating user status:', error);
            toast.error(t('errorUpdatingStatus'));
        }
    };

    const getStatusButtonText = () => {
        if (!user) return t('activate');

        switch (user.status) {
            case 'enabled': return t('deactivate');
            case 'disabled': return t('reactivate');
            default: return t('activate');
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'enabled': return '#10b981';
            case 'disabled': return '#ef4444';
            default: return '#6b7280';
        }
    };

    const getSessionStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'success': return '#10b981';
            case 'failed': return '#ef4444';
            default: return '#f59e0b';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString || dateString === 'N/A') return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString(lang, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

      const handleOpenResetModal = () => {
    setShowResetModal(true);
    setResetMessage('');
  };

  // Fonction pour resetter le mot de passe
  const handleResetPassword = async () => {
    if (!id) return;

    setIsResetting(true);
    setResetMessage('');

    try {
      const response = await api.get(`/users/${id}/generate/password`);

      if (response.status === 200 || response.status === 201 || response.status === 202) {
        setResetMessage(t('resetPasswordSuccess'));
        // Fermer automatiquement après 3 secondes
        setTimeout(() => {
          setShowResetModal(false);
          setResetMessage('');
        }, 3000);
      } else {
        setResetMessage(response.data.result?.message || t('resetPasswordError'));
      }
    } catch (error) {
      console.error(t('resetPasswordApiError'), error);
      setResetMessage(t('resetPasswordError'));
    } finally {
      setIsResetting(false);
    }
  };

  const handleCloseResetModal = () => {
    setShowResetModal(false);
    setResetMessage('');    
  };

    const styles = {
        container: {
            padding: '8px 24px',
            background: '#f9fafb',
            minHeight: '100vh'
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '8px'
        },
        backButton: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 12px',
            backgroundColor: 'transparent',
            color: '#4b5563',
            border: '1px solid #d1d5db',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            borderRadius: '8px',
            transition: 'all 0.2s ease',
            marginBottom: '4px',
            '&:hover': {
                backgroundColor: '#f3f4f6',
                borderColor: '#9ca3af'
            }
        },
        title: {
            fontSize: '28px',
            fontWeight: 'bold',
            color: '#1f2937',
            margin: 0
        },
        loadingContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px'
        },
        loadingText: {
            fontSize: '18px',
            color: '#6b7280'
        },
        section: {
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        },
        sectionHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
            paddingBottom: '16px',
            borderBottom: '1px solid #e5e7eb'
        },
        sectionTitle: {
            fontSize: '20px',
            fontWeight: '600',
            color: '#1f2937',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
        },
        statusRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
            padding: '16px',
            background: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
        },
        status: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '16px',
            fontWeight: '600'
        },
        userSummary: {
            display: 'flex',
            alignItems: 'center',
            gap: '24px',
            marginBottom: '24px',
            padding: '24px',
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            borderRadius: '12px',
            color: 'white'
        },
        avatar: {
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '32px',
            fontWeight: 'bold',
            color: 'white'
        },
        userInfo: {
            flex: 1
        },
        userName: {
            fontSize: '28px',
            fontWeight: 'bold',
            margin: '0 0 8px 0',
            color: 'white'
        },
        userEmail: {
            fontSize: '16px',
            opacity: '0.9',
            margin: 0,
            color: 'white'
        },
        infoGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px'
        },
        infoItem: {
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            padding: '16px',
            background: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            transition: 'all 0.2s ease',
            '&:hover': {
                background: '#f3f4f6',
                transform: 'translateY(-2px)',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }
        },
        infoItemHeader: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '4px'
        },
        infoIcon: {
            color: '#3b82f6'
        },
        infoLabel: {
            fontSize: '12px',
            color: '#6b7280',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
        },
        infoValue: {
            fontSize: '16px',
            color: '#1f2937',
            fontWeight: '500',
            lineHeight: '1.5'
        },
        infoSubValue: {
            fontSize: '14px',
            color: '#4b5563',
            fontWeight: '400'
        },
        buttonGroup: {
            display: 'flex',
            gap: '12px',
            alignItems: 'center'
        },
        primaryButton: {
            padding: '10px 20px',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontWeight: '500',
            transition: 'all 0.2s ease',
            '&:hover': {
                background: '#2563eb',
                transform: 'translateY(-1px)'
            }
        },
        secondaryButton: {
            padding: '10px 20px',
            background: '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontWeight: '500',
            transition: 'all 0.2s ease',
            '&:hover': {
                background: '#0da271',
                transform: 'translateY(-1px)'
            }
        },
        statusButton: {
            padding: '10px 20px',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            transition: 'all 0.2s ease',
            background: (user?.status === 'enabled') ? '#ef4444' :
                (user?.status === 'disabled') ? '#10b981' : '#6b7280',
            color: 'white',
            '&:hover': {
                opacity: '0.9',
                transform: 'translateY(-1px)'
            }
        },
        sessionSummary: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '16px',
            marginBottom: '24px',
            padding: '20px',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            borderRadius: '12px',
            color: 'white'
        },
        summaryItem: {
            textAlign: 'center',
            padding: '16px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            backdropFilter: 'blur(10px)'
        },
        summaryValue: {
            fontSize: '28px',
            fontWeight: 'bold',
            marginBottom: '8px',
            color: 'white'
        },
        summaryLabel: {
            fontSize: '14px',
            opacity: '0.9',
            color: 'white'
        },
        noData: {
            textAlign: 'center',
            padding: '60px 20px',
            color: '#6b7280',
            background: '#f9fafb',
            borderRadius: '8px',
            border: '2px dashed #d1d5db'
        },
        simpleTableContainer: {
            overflowX: 'auto',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        },
        simpleTable: {
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: '0',
            background: 'white'
        },
        tableHeader: {
            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
        },
        tableHeaderCell: {
            padding: '16px',
            textAlign: 'left',
            color: 'white',
            fontWeight: '700',
            fontSize: '16px',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
        },
        tableRow: {
            borderBottom: '1px solid #e5e7eb',
            transition: 'background 0.2s ease',
            '&:hover': {
                background: '#f9fafb'
            }
        },
        tableCell: {
            padding: '16px',
            fontSize: '14px',
            color: '#374151',
            borderBottom: '1px solid #e5e7eb'
        },
        statusBadge: {
            padding: '6px 12px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '600',
            color: 'white',
            textTransform: 'uppercase',
            display: 'inline-block',
            minWidth: '80px',
            textAlign: 'center'
        },
        sessionStatusBadge: {
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: '600',
            color: 'white',
            display: 'inline-block',
            textAlign: 'center'
        },
        actionButtons: {
            display: 'flex',
            gap: '8px',
        },
        editButton: {
            padding: '8px 16px',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '12px',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.2s ease',
        },
        resetPasswordButton: {
            padding: '8px 16px',
            background: '#8b5cf6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '12px',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.2s ease',
        },

    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    },
    modalContent: {
      background: 'white',
      borderRadius: '12px',
      padding: '32px',
      width: '90%',
      maxWidth: '500px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px'
    },
    modalTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1f2937',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    closeButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#6b7280',
      padding: '8px',
      borderRadius: '6px',
      transition: 'all 0.2s ease',
      fontFamily: 'inherit'
    },
    modalBody: {
      marginBottom: '24px',
      color: '#374151',
      lineHeight: '1.6'
    },
    modalMessage: {
      padding: '12px',
      borderRadius: '8px',
      marginBottom: '16px',
      fontSize: '14px',
      lineHeight: '1.5'
    },
    successMessage: {
      background: '#d1fae5',
      color: '#065f46',
      border: '1px solid #a7f3d0',
      
    },
    errorMessage: {
      background: '#fee2e2',
      color: '#991b1b',
      border: '1px solid #fecaca'
    },
    warningMessage: {
      background: '#fef3c7',
      color: '#92400e',
      border: '1px solid #fde68a'
    },
    modalButtons: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end'
    },
    cancelButton: {
      padding: '12px 24px',
      background: '#6b7280',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s ease',
      fontFamily: 'inherit'
    },
    resetButton: {
      padding: '12px 24px',
      background: '#ef4444',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s ease',
      fontFamily: 'inherit'
    },
    resetButtonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed'
    }
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <button
                    onClick={() => navigate('/users')}
                    style={styles.backButton}
                >
                    <SquareChevronLeft size={20} />
                    {t('backToUsers')}
                </button>
                <div style={styles.loadingContainer}>
                    <div style={styles.loadingText}>{t('loading')}...</div>
                </div>
            </div>
        );
    }

    if (!user) {
        return (
            <div style={styles.container}>
                <button
                    onClick={() => navigate('/users')}
                    style={styles.backButton}
                >
                    <SquareChevronLeft size={20} />
                    {t('backToUsers')}
                </button>
                <div style={styles.noData}>
                    <p>{t('userNotFound')}</p>
                </div>
            </div>
        );
    }

    // Get user initials for avatar
    const getUserInitials = () => {
        const names = user.full_name.split(' ');
        if (names.length >= 2) {
            return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
        }
        return user.full_name.substring(0, 2).toUpperCase();
    };

    // Calculate session stats
    const sessionStats = {
        total: sessionLogs.length,
        successful: sessionLogs.filter(log => log.status?.toLowerCase() === 'success').length,
        failed: sessionLogs.filter(log => log.status?.toLowerCase() === 'failed').length,
        last24h: sessionLogs.filter(log => {
            const logDate = new Date(log.date);
            const now = new Date();
            const diffHours = (now - logDate) / (1000 * 60 * 60);
            return diffHours <= 24;
        }).length
    };

    return (
        <div style={styles.container}>
            {/* Header with back button */}
            <div style={styles.header}>
                <button
                    onClick={() => navigate('/users')}
                    style={styles.backButton}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                        e.currentTarget.style.borderColor = '#9ca3af';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.borderColor = '#d1d5db';
                    }}
                >
                    <SquareChevronLeft size={20} />
                    {t('backToUsers')}
                </button>
            </div>

            {/* User Summary Section */}
            <div style={styles.userSummary}>
                <div style={styles.avatar}>
                    {getUserInitials()}
                </div>
                <div style={styles.userInfo}>
                    <h1 style={styles.userName}>{user.full_name}</h1>
                    <p style={styles.userEmail}>{user.email}</p>
                    <p style={{ marginTop: '8px', fontSize: '14px', opacity: '0.8' }}>
                        {t('user')} ID: {user.id} • {t('registeredOn')}: {user.created_at}
                    </p>
                </div>
            </div>

            {/* User Information Section */}
            <div style={styles.section}>
                <div style={styles.sectionHeader}>
                    <h2 style={styles.sectionTitle}>
                        <User size={24} color="#3b82f6" />
                        {t('userInformation')}
                    </h2>
                </div>

                {/* Status Row + Button */}
                <div style={styles.statusRow}>
                    <div style={styles.status}>
                        {t('status')}:
                        <span style={{
                            color: getStatusColor(user.status),
                            textTransform: 'capitalize'
                        }} title={user.status}>
                            {user.status}
                        </span>
                        {user.last_login !== 'N/A' && (
                            <span style={styles.infoSubValue}>
                                • {t('lastLogin')}: {formatDate(user.last_login)}
                            </span>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '12px' }}>
                        <button
                            style={styles.statusButton}
                            onClick={toggleUserStatus}
                        >
                            {getStatusButtonText()}
                        </button>
                        <button
                            style={styles.primaryButton}
                            onClick={handleEdit}
                        >
                            <Edit size={16} />
                            {t('editUser')}
                        </button>
                        <button
                            style={styles.secondaryButton}
                            onClick={() => {
                                handleOpenResetModal();
                            }}
                        >
                            <Key size={16} />
                            {t('resetPassword')}
                        </button>
                    </div>
                </div>

                {/* Information Grid */}
                <div style={styles.infoGrid}>
                    <div style={styles.infoItem}>
                        <div style={styles.infoItemHeader}>
                            <User size={18} style={styles.infoIcon} />
                            <span style={styles.infoLabel}>{t('name')}</span>
                        </div>
                        <span style={styles.infoValue} title={user.full_name}>{user.full_name}</span>
                    </div>

                    <div style={styles.infoItem}>
                        <div style={styles.infoItemHeader}>
                            <Mail size={18} style={styles.infoIcon} />
                            <span style={styles.infoLabel}>{t('email')}</span>
                        </div>
                        <span style={styles.infoValue} title={user.email}>{user.email}</span>
                    </div>

                    <div style={styles.infoItem}>
                        <div style={styles.infoItemHeader}>
                            <Phone size={18} style={styles.infoIcon} />
                            <span style={styles.infoLabel}>{t('phone')}</span>
                        </div>
                        <span style={styles.infoValue} title={user.phone}>{user.phone}</span>
                    </div>

                    <div style={styles.infoItem}>
                        <div style={styles.infoItemHeader}>
                            <MapPin size={18} style={styles.infoIcon} />
                            <span style={styles.infoLabel}>{t('address')}</span>
                        </div>
                        <span style={styles.infoValue} title={user.address}>{user.address}</span>
                    </div>

                    {/* <div style={styles.infoItem}>
                        <div style={styles.infoItemHeader}>
                            <Shield size={18} style={styles.infoIcon} />
                            <span style={styles.infoLabel}>{t('role')}</span>
                        </div>
                        <span style={styles.infoValue} title={user.role}>
                            {user.role}
                        </span>
                    </div> */}

                    <div style={styles.infoItem}>
                        <div style={styles.infoItemHeader}>
                            <Calendar size={18} style={styles.infoIcon} />
                            <span style={styles.infoLabel}>{t('creationDate')}</span>
                        </div>
                        <span style={styles.infoValue} title={user.created_at}>
                            {user.created_at}
                        </span>
                    </div>

                    {/* <div style={styles.infoItem}>
                        <div style={styles.infoItemHeader}>
                            <Activity size={18} style={styles.infoIcon} />
                            <span style={styles.infoLabel}>{t('lastActivity')}</span>
                        </div>
                        <span style={styles.infoValue} title={user.last_login}>
                            {user.last_login === 'N/A' ? 'N/A' : formatDate(user.last_login)}
                        </span>
                    </div>

                    <div style={styles.infoItem}>
                        <div style={styles.infoItemHeader}>
                            <Key size={18} style={styles.infoIcon} />
                            <span style={styles.infoLabel}>{t('permissions')}</span>
                        </div>
                        <span style={styles.infoValue} title={user.permissions?.join(', ')}>
                            {user.permissions?.length > 0 ? user.permissions?.join(', ') : 'Basic User'}
                        </span>
                    </div> */}
                </div>
            </div>

            {/* Session Logs Section */}
            <div style={styles.section}>
                <div style={styles.sectionHeader}>
                    <h2 style={styles.sectionTitle}>
                        <History size={24} color="#8b5cf6" />
                        {t('sessionLogs')}
                        <span style={{
                            fontSize: '14px',
                            color: '#6b7280',
                            marginLeft: '8px'
                        }}>
                            ({sessionLogs.length})
                        </span>
                    </h2>
                    {sessionLogs.length > 0 && (
                        <div style={styles.buttonGroup}>
                            <button style={styles.secondaryButton}>
                                <Download size={16} />
                                {t('exportLogs')}
                            </button>
                        </div>
                    )}
                </div>

                {/* Session Summary */}
                {sessionLogs.length > 0 && (
                    <div style={styles.sessionSummary}>
                        <div style={styles.summaryItem}>
                            <div style={styles.summaryValue}>{sessionStats.total}</div>
                            <div style={styles.summaryLabel}>{t('totalSessions')}</div>
                        </div>
                        <div style={styles.summaryItem}>
                            <div style={styles.summaryValue}>{sessionStats.successful}</div>
                            <div style={styles.summaryLabel}>{t('successful')}</div>
                        </div>
                        <div style={styles.summaryItem}>
                            <div style={styles.summaryValue}>{sessionStats.failed}</div>
                            <div style={styles.summaryLabel}>{t('failed')}</div>
                        </div>
                        <div style={styles.summaryItem}>
                            <div style={styles.summaryValue}>{sessionStats.last24h}</div>
                            <div style={styles.summaryLabel}>{t('last24Hours')}</div>
                        </div>
                    </div>
                )}

                {sessionLogs.length > 0 ? (
                    <div style={styles.simpleTableContainer}>
                        <table style={styles.simpleTable}>
                            <thead>
                                <tr style={styles.tableHeader}>
                                    <th style={styles.tableHeaderCell}>{t('dateTime')}</th>
                                    <th style={styles.tableHeaderCell}>{t('status')}</th>
                                    <th style={styles.tableHeaderCell}>IP {t('address')}</th>
                                    <th style={styles.tableHeaderCell}>{t('message')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sessionLogs.map((log, index) => (
                                    <tr key={index} style={styles.tableRow}>
                                        <td style={styles.tableCell} title={log.date}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <Clock size={14} color="#6b7280" />
                                                {log.date}
                                            </div>
                                        </td>
                                        <td style={styles.tableCell}>
                                            <span style={{
                                                ...styles.sessionStatusBadge,
                                                background: getSessionStatusColor(log.status)
                                            }}>
                                                {log.status === 'success' ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <CheckCircle size={12} />
                                                        {t('success')}
                                                    </div>
                                                ) : (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <XCircle size={12} />
                                                        {t('failed')}
                                                    </div>
                                                )}
                                            </span>
                                        </td>
                                        <td style={styles.tableCell} title={log.ip}>
                                            <code>{log.ip}</code>
                                        </td>
                                        <td style={styles.tableCell} title={log.message}>
                                            {log.message}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div style={styles.noData}>
                        <History size={48} color="#9ca3af" />
                        <p style={{ marginTop: '16px' }}>{t('noSessionLogs')}</p>
                        <p style={{ fontSize: '14px', color: '#9ca3af', marginTop: '8px' }}>
                            {t('sessionLogsWillAppear')}
                        </p>
                    </div>
                )}
            </div>

            {/* Quick Actions Section
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>
            <Activity size={24} color="#10b981" />
            {t('quickActions')}
          </h2>
        </div>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px' 
        }}>
          <button 
            style={styles.primaryButton}
            onClick={handleEdit}
          >
            <Edit size={16} />
            {t('editProfile')}
          </button>
          <button 
            style={styles.secondaryButton}
            onClick={() => {
              toast.info(t('sendWelcomeEmailFeature'));
            }}
          >
            <Mail size={16} />
            {t('sendWelcomeEmail')}
          </button>
          <button 
            style={{
              ...styles.primaryButton,
              background: '#f59e0b'
            }}
            onClick={() => {
              toast.info(t('viewActivityLogFeature'));
            }}
          >
            <Activity size={16} />
            {t('viewFullActivity')}
          </button>
          <button 
            style={{
              ...styles.primaryButton,
              background: '#8b5cf6'
            }}
            onClick={() => {
              toast.info(t('managePermissionsFeature'));
            }}
          >
            <Shield size={16} />
            {t('managePermissions')}
          </button>
        </div>
      </div> */}

            <ToastContainer position="bottom-right" />


            {showUserModal && (
                <UserModal
                    user={user}
                    type={'edit'}
                    onClose={() => setShowUserModal(false)}
                    onSave={(userData) => {

                        handleEditUser(userData);
                        setShowUserModal(false);
                    }}
                    lang={lang}
                />
            )}

                  {showResetModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                <Key size={24} color="#ef4444" />
                {t('resetPasswordConfirm')}
              </h2>
              <button
                style={styles.closeButton}
                onClick={handleCloseResetModal}
                disabled={isResetting}
                onMouseEnter={(e) => !isResetting && (e.target.style.background = '#f3f4f6')}
                onMouseLeave={(e) => !isResetting && (e.target.style.background = 'transparent')}
              >
                <X size={20} />
              </button>
            </div>

            {resetMessage ? (
              <div style={{
                ...styles.modalMessage,
                ...(resetMessage.includes(t('success')) ? styles.successMessage : 
                    resetMessage.includes(t('error')) ? styles.errorMessage : styles.warningMessage)
              }}>
                {resetMessage}
              </div>
            ) : (
              <div style={styles.modalBody}>
                <p style={{ marginBottom: '12px' }}>
                  {t('resetPasswordConfirmation')} <strong>ID: {id} {t('name')}: {user?.full_name} </strong>?
                </p>
                <p style={{ fontSize: '14px', color: '#6b7280' }}>
                  {t('resetPasswordInstructions')}
                </p>
              </div>
            )}

            <div style={styles.modalButtons}>
              {!resetMessage && (
                <>
                  <button
                    style={styles.cancelButton}
                    onClick={handleCloseResetModal}
                    disabled={isResetting}
                    onMouseEnter={(e) => !isResetting && (e.target.style.background = '#4b5563')}
                    onMouseLeave={(e) => !isResetting && (e.target.style.background = '#6b7280')}
                  >
                    {t('cancel')}
                  </button>
                  <button
                    style={{
                      ...styles.resetButton,
                      ...(isResetting && styles.resetButtonDisabled)
                    }}
                    onClick={handleResetPassword}
                    disabled={isResetting}
                    onMouseEnter={(e) => !isResetting && (e.target.style.background = '#dc2626')}
                    onMouseLeave={(e) => !isResetting && (e.target.style.background = '#ef4444')}
                  >
                    {isResetting ? t('resetting') : t('confirmReset')}
                  </button>
                </>
              )}
              
              {resetMessage && (
                <button
                  style={styles.cancelButton}
                  onClick={handleCloseResetModal}
                  onMouseEnter={(e) => e.target.style.background = '#4b5563'}
                  onMouseLeave={(e) => e.target.style.background = '#6b7280'}
                >
                  {t('close')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
        </div>


    );
};

export default UserDetails;